/**
 * Copyright (c) 2025 - Present. Blend Labs, Inc. All rights reserved
 * Blend Confidential - Restricted
 */
import { useCallback, useRef } from 'react';
import { diagnostics } from '@utils/diagnostics';

interface UseSoundEffectOptions {
  /**
   * The URL or path to the sound file
   */
  soundFile: string;
  /**
   * Volume of the sound (0.0 to 1.0)
   * @default 1.0
   */
  volume?: number;
}

/**
 * Hook for playing sound effects in the background
 * @param options Configuration options for the sound effect
 * @returns Object containing play and stop functions
 */
export function useSoundEffect({ soundFile, volume = 1.0 }: UseSoundEffectOptions) {
  const audioRef = useRef<HTMLAudioElement | null>(null);

  // Initialize audio element
  const initAudio = useCallback(() => {
    if (!audioRef.current) {
      const audio = new Audio(soundFile);
      audio.loop = false;
      audio.volume = volume;
      audioRef.current = audio;
    }
  }, [soundFile, volume]);

  // Play the sound
  const play = useCallback(async () => {
    try {
      initAudio();
      if (audioRef.current) {
        // Reset the audio to the beginning if it was already played
        audioRef.current.currentTime = 0;
        await audioRef.current.play();
      }
    } catch (error) {
      diagnostics.error(new Error('Error playing sound effect'), {
        soundFile,
        volume,
      });
    }
  }, [initAudio]);

  // Stop the sound
  const stop = useCallback(() => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
    }
  }, []);

  return {
    play,
    stop,
  };
}
